import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { boqCategoriesApi, sowCategoriesApi, type Category, type CreateCategoryInput } from '../../api/boqMasters.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function CategoryListPage({ kind }: { kind: 'boq' | 'sow' }) {
  const api = kind === 'boq' ? boqCategoriesApi : sowCategoriesApi;
  const qkey = `${kind}-categories`;
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState<CreateCategoryInput>({ name: '', parentId: null });
  const [deleting, setDeleting] = useState<Category | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: [qkey, page, limit, search], queryFn: () => api.list({ page, limit, search: search || undefined }) });
  const parents = useQuery({ queryKey: [qkey, 'parents'], queryFn: () => api.list({ limit: 100 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: [qkey] });

  const save = useMutation({
    mutationFn: () => (editing ? api.update(editing.id, form) : api.create(form)),
    onSuccess: () => { setOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({
    mutationFn: (id: number) => api.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const topLevel = (parents.data?.data ?? []).filter((c) => c.parentId === null);
  const columns: Column<Category>[] = [
    { key: 'code', label: 'Code', width: 110 },
    { key: 'name', label: 'Name' },
    { key: 'parentId', label: 'Parent', render: (r) => topLevel.find((p) => p.id === r.parentId)?.name ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  const openNew = () => { setEditing(null); setForm({ name: '', parentId: null }); setError(null); setOpen(true); };
  const openEdit = (c: Category) => { setEditing(c); setForm({ name: c.name, parentId: c.parentId, status: c.status }); setError(null); setOpen(true); };

  return (
    <>
      <PageHeader
        title={kind === 'boq' ? 'BOQ Category Master' : 'SOW Category Master'}
        action={hasPermission('category.create') && <Button startIcon={<Add />} variant="contained" onClick={openNew}>New Category</Button>}
      />
      <Stack direction="row" mb={2}><TextField size="small" placeholder="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} /></Stack>
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('category.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('category.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required fullWidth />
            <TextField select label="Parent (optional)" value={form.parentId ?? ''} onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value ? Number(e.target.value) : null }))} fullWidth>
              <MenuItem value="">— Top level —</MenuItem>
              {topLevel.filter((p) => p.id !== editing?.id).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => save.mutate()} disabled={!form.name}>Save</Button></DialogActions>
      </Dialog>
      <ConfirmDialog open={Boolean(deleting)} title="Delete category" message={`Delete ${deleting?.name}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
